'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { logger } from '@/lib/logger'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Component, ErrorInfo, ReactNode } from 'react'

const CHUNK_RELOAD_KEY = 'exchange-order-chunk-reload-at'
// Trong khoảng này coi như đã reload rồi mà vẫn lỗi -> không reload nữa để tránh vòng lặp
const CHUNK_RELOAD_COOLDOWN_MS = 10_000

const isChunkLoadError = (error: Error) =>
  error.name === 'ChunkLoadError' ||
  /Failed to load chunk|Loading (CSS )?chunk [\w-]+ failed/i.test(error.message)

// Tự reload trang 1 lần khi tải chunk thất bại (deploy mới, mạng chập chờn...)
const reloadOnceForChunkError = (): boolean => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
    if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return false

    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  } catch {
    // sessionStorage không khả dụng (private mode...) -> không tự reload để tránh vòng lặp
    return false
  }

  window.location.reload()
  return true
}

interface Props {
  children: ReactNode
  fallback?: (error: Error, resetError: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ExchangeOrderErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return

    console.error(
      'Return Order Error Boundary caught an error:',
      error,
      errorInfo
    )

    logger.captureException(error as Error, {
      context: {
        errorInfo,
      },
    })

    // Here you could send error to logging service
    // logErrorToService(error, errorInfo)
  }

  resetError = () => {
    this.setState({ hasError: false, error: null })
  }

  // Chunk lỗi đã bị cache trong module registry, render lại không giúp được -> phải reload trang
  handleRetry = () => {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      window.location.reload()
      return
    }

    this.resetError()
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError)
      }

      return (
        <section className='flex min-h-[50vh] items-center justify-center p-4 sm:p-6 lg:p-8'>
          <Card className='w-full max-w-md sm:max-w-lg lg:max-w-2xl'>
            <CardContent className='pt-6'>
              <div className='text-center'>
                <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 sm:mb-6 sm:h-16 sm:w-16 lg:h-20 lg:w-20'>
                  <AlertTriangle className='h-6 w-6 text-destructive sm:h-8 sm:w-8 lg:h-10 lg:w-10' />
                </div>

                <h2 className='mb-2 text-lg font-semibold text-foreground sm:mb-4 sm:text-xl lg:text-2xl'>
                  Đã xảy ra lỗi
                </h2>

                <p className='mb-6 text-sm leading-relaxed text-muted-foreground sm:mb-8 sm:text-base lg:text-lg'>
                  {this.state.error.message ||
                    'Có lỗi xảy ra khi tải trang. Vui lòng thử lại sau.'}
                </p>

                <div className='flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4'>
                  <Button
                    variant='black'
                    onClick={this.handleRetry}
                    className='flex items-center gap-2'
                  >
                    <RefreshCw className='h-4 w-4' />
                    Thử lại
                  </Button>

                  <Button
                    variant='outline-neutral'
                    onClick={() => (window.location.href = '/doi-tra')}
                  >
                    Quay lại
                  </Button>
                </div>

                {/* Additional help information for larger screens */}
                <div className='mt-6 rounded-lg bg-muted/50 p-4 lg:mt-8 lg:p-6'>
                  <p className='text-sm text-muted-foreground lg:text-base'>
                    Nếu lỗi vẫn tiếp tục xảy ra, vui lòng liên hệ bộ phận hỗ trợ
                    khách hàng của chúng tôi.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )
    }

    return this.props.children
  }
}
